import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box, Chip, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExpandMore } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from "@/auth/useAuth";
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { useRolePinGroups, withRolePins } from '@/hooks/useHomeLayout';
import { tk } from '@/theme/trektopiaTokens';
import { navGroupsFor, NAV_SECTIONS, type NavItem, type NavGroup } from './navConfig';
import { useWorkspace } from '@/stores/WorkspaceStore';
import { APP_NAME } from '@/config/productBrand';

export const SIDEBAR_WIDTH = tk.shell.width;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

const EXPANDED_KEY = 'st.nav.expandedGroup';

/**
 * The navigation shell as the approved Module 0 screens draw it: a white rail with the TravelERP mark,
 * Home and the daily-work modules, a light-blue pill for the current page, and the SETUP entries at the
 * foot under their heading.
 */
const SHELL = {
  activeText: tk.shell.activeText,
  bg: tk.shell.bg,
  text: tk.shell.text,
  textMuted: tk.shell.textMuted,
  heading: tk.shell.textMuted,
  active: tk.shell.active,
  activeSub: tk.shell.activeSub,
  hover: tk.shell.hover,
  divider: tk.shell.border,
};

/** A nav target's path and query — "/travel-masters?tab=room-types". */
function splitTo(to: string): { path: string; query: URLSearchParams } {
  const [path, query = ''] = to.split('?');
  return { path, query: new URLSearchParams(query) };
}

/** Whether a nav target covers where the browser is: the same path (or below it) and every query value it names. */
function covers(to: string, pathname: string, search: string): boolean {
  const { path, query } = splitTo(to);
  if (!(pathname === path || pathname.startsWith(`${path}/`))) return false;
  const here = new URLSearchParams(search);
  for (const [k, v] of query) if (here.get(k) !== v) return false;
  return true;
}

/** A navigable leaf: `to` is guaranteed present. */
type LeafItem = NavItem & { to: string };

function readExpanded(): string | null {
  try { return window.localStorage.getItem(EXPANDED_KEY); } catch { return null; }
}
function writeExpanded(label: string | null) {
  try {
    if (label) window.localStorage.setItem(EXPANDED_KEY, label);
    else window.localStorage.removeItem(EXPANDED_KEY);
  } catch { /* private mode, quota — never worth breaking navigation over */ }
}

/** Every navigable leaf under an item tree. */
function leavesOf(items: NavItem[]): LeafItem[] {
  return items.flatMap((item) => (item.children?.length
    ? leavesOf(item.children)
    : item.to ? [item as LeafItem] : []));
}

/**
 * Section-based navigation sidebar: a flat list of entries under five uppercase headings.
 *
 * <p>
 * Entries are a single-open accordion — opening one closes the previous — and an entry either links
 * straight to a route (Dashboard, Enquiry, Quotes) or expands into its menu. One optional submenu level
 * below that, never deeper. The active route auto-expands whatever contains it.
 * </p>
 * <p>
 * RBAC and tenant feature flags are applied here, unchanged: an entry whose every leaf is hidden by
 * permissions disappears rather than opening onto an empty list.
 * </p>
 */
export default function Sidebar({ onNavigate, collapsed = false }: { onNavigate?: () => void; collapsed?: boolean } = {}) {
  const { hasAnyPermission } = useAuth();
  const { isFeatureEnabled } = useFeatureGate();
  const { workspace } = useWorkspace();
  const location = useLocation();
  const pathname = location.pathname;
  const { search } = location;
  // FR-NAV-03/04: the daily items the person's role pins, already permission- and feature-filtered.
  const rolePins = useRolePinGroups();

  /**
   * The visible tree, after permissions and feature flags.
   *
   * An entry is kept when it is a direct link the person may follow, when it is marked coming-soon
   * (nothing to authorise — there is no page yet), or when at least one leaf under it survives.
   */
  const visible = useMemo(() => {
    const allowed = (item: NavItem): boolean =>
      (!item.perms?.length || hasAnyPermission(item.perms)) && isFeatureEnabled(item.feature);

    const filterItems = (items: NavItem[]): NavItem[] => items.reduce<NavItem[]>((acc, item) => {
      if (item.children?.length) {
        const kids = filterItems(item.children);
        if (kids.length) acc.push({ ...item, children: kids });
        return acc;
      }
      if (item.to && allowed(item)) acc.push(item);
      return acc;
    }, []);

    // Workspace first, permissions second, and the order matters: the workspace decides what this
    // menu is ABOUT, and RBAC then decides how much of it this person may see. Filtering the other
    // way round would let a B2C session's permissions decide whether a B2B section exists.
    const groups = navGroupsFor(workspace).reduce<NavGroup[]>((acc, group) => {
      if (group.comingSoon) { acc.push(group); return acc; }

      if (group.to) {
        if (!group.perms?.length || hasAnyPermission(group.perms)) acc.push(group);
        return acc;
      }

      const kids = filterItems(group.children ?? []);
      if (kids.length) acc.push({ ...group, children: kids });
      return acc;
    }, []);
    return withRolePins(groups, rolePins);
  }, [hasAnyPermission, isFeatureEnabled, workspace, rolePins]);

  /**
   * The one leaf the browser is on: of every target that covers the address, the most specific —
   * so /hotels/contracts lights Rate Contracts, not Hotel Master as well.
   */
  const currentTo = useMemo(() => {
    const targets = visible.flatMap((group) => (group.to ? [group.to] : leavesOf(group.children ?? []).map((l) => l.to)));
    return targets
      .filter((to) => covers(to, pathname, search))
      .sort((a, b) => splitTo(b).path.length - splitTo(a).path.length || b.length - a.length)[0] ?? null;
  }, [visible, pathname, search]);

  /** Which entry contains the current route — what auto-expands, and what reads as active. */
  const activeGroup = useMemo(() => visible.find((group) => (group.to
    ? group.to === currentTo || (group.activeOn ?? []).some((p) => pathname === p || pathname.startsWith(`${p}/`))
    : leavesOf(group.children ?? []).some((leaf) => leaf.to === currentTo))
  )?.label ?? null, [visible, currentTo, pathname]);

  const [expanded, setExpanded] = useState<string | null>(() => readExpanded());

  // The route wins over whatever was last opened by hand: arriving somewhere by link or refresh should
  // reveal where you are rather than leave the rail describing a different part of the product.
  useEffect(() => {
    if (activeGroup) {
      setExpanded(activeGroup);
      writeExpanded(activeGroup);
    }
  }, [activeGroup]);

  const toggle = (label: string) => {
    const next = expanded === label ? null : label;
    setExpanded(next);
    writeExpanded(next);
  };

  const isCurrent = (to: string) => to === currentTo;

  return (
    <Box sx={{
      width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      bgcolor: SHELL.bg,
      color: SHELL.text,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'width .25s ease',
    }}>
      {/* The product mark at the top of the rail, as designed; the company is named in the header. */}
      <Box
        component={RouterLink}
        to="/dashboard"
        onClick={onNavigate}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: collapsed ? 0 : 2, pt: collapsed ? 1.5 : 2.25, pb: collapsed ? 1.25 : 2.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
          textDecoration: 'none', color: 'inherit', flexShrink: 0,
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0, bgcolor: SHELL.activeText, color: '#fff',
          display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 16,
        }}>
          {APP_NAME.charAt(0)}
        </Box>

        {!collapsed && (
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
            {APP_NAME}
          </Typography>
        )}
      </Box>

      {/* ---- Navigation ---- */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        px: collapsed ? 1 : 1.5,
        pb: 1,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: alpha('#000000', 0.12), borderRadius: 8 },
      }}>
        <List disablePadding>
          {/* Home and the daily-work modules. */}
          {visible.filter((g) => !g.section).map((group) => (
            <Entry
              key={group.label}
              group={group}
              collapsed={collapsed}
              expanded={expanded === group.label}
              activeGroup={activeGroup}
              isCurrent={isCurrent}
              onToggle={toggle}
              onNavigate={onNavigate}
            />
          ))}

        </List>
      </Box>

      {/* SETUP, at the foot of the rail under its heading. */}
      <Box sx={{ flexShrink: 0, px: collapsed ? 1 : 1.5, pb: 1.5, borderTop: 1, borderColor: SHELL.divider, pt: 1 }}>
        <List disablePadding>
          {NAV_SECTIONS.map((section) => {
            const entries = visible.filter((g) => g.section === section);
            if (!entries.length) return null;

            return (
              <Box key={section}>
                {!collapsed && (
                  <Typography sx={{ px: 1.25, pt: 0.5, pb: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: SHELL.textMuted }}>
                    {section}
                  </Typography>
                )}

                {entries.map((group) => (
                  <Entry
                    key={group.label}
                    group={group}
                    collapsed={collapsed}
                    expanded={expanded === group.label}
                    activeGroup={activeGroup}
                    isCurrent={isCurrent}
                    onToggle={toggle}
                    onNavigate={onNavigate}
                  />
                ))}
              </Box>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}

/**
 * One sidebar entry: a direct link, a coming-soon placeholder, or an expandable menu.
 */
function Entry({ group, collapsed, expanded, activeGroup, isCurrent, onToggle, onNavigate }: {
  group: NavGroup;
  collapsed: boolean;
  expanded: boolean;
  activeGroup: string | null;
  isCurrent: (to: string) => boolean;
  onToggle: (label: string) => void;
  onNavigate?: () => void;
}) {
  const active = activeGroup === group.label;

  const row = (extra?: ReactNode) => (
    <>
      <ListItemIcon sx={{
        minWidth: collapsed ? 0 : 36,
        color: active ? SHELL.activeText : SHELL.text,
        '& .MuiSvgIcon-root': { fontSize: 22 },
      }}>
        {group.icon}
      </ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            primary={group.label}
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              color: active ? SHELL.activeText : SHELL.text,
              noWrap: true,
            }}
          />
          {extra}
        </>
      )}
    </>
  );

  const buttonSx = {
    borderRadius: `${tk.radius.md}px`,
    minHeight: collapsed ? 36 : 40,
    py: collapsed ? 0.4 : 0.85,
    px: collapsed ? 1 : 1.25,
    mb: collapsed ? 0.15 : 0.4,
    justifyContent: collapsed ? 'center' : 'flex-start',
    bgcolor: active ? SHELL.active : 'transparent',
    '&:hover': { bgcolor: active ? SHELL.active : SHELL.hover },
  } as const;

  // ---- Not built yet: shown because the design shows it, but it goes nowhere. ----
  if (group.comingSoon) {
    return (
      <Tooltip title={collapsed ? `${group.label} — coming soon` : 'Coming soon'} placement="right">
        <ListItemButton disableRipple sx={{ ...buttonSx, opacity: 0.55, cursor: 'default' }}>
          {row(group.tag ? (
            <Chip
              label={group.tag}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700, color: '#fff',
                bgcolor: SHELL.active, '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ) : (
            <Typography sx={{ fontSize: 10, color: SHELL.textMuted }}>soon</Typography>
          ))}
        </ListItemButton>
      </Tooltip>
    );
  }

  // ---- Direct link ----
  if (group.to) {
    return (
      <Tooltip title={collapsed ? group.label : ''} placement="right">
        <ListItemButton
          component={RouterLink}
          to={group.to}
          onClick={onNavigate}
          sx={buttonSx}
        >
          {row(group.badge ? <Badge value={group.badge} active={active} /> : null)}
        </ListItemButton>
      </Tooltip>
    );
  }

  // ---- Expandable menu ----
  return (
    <>
      <Tooltip title={collapsed ? group.label : ''} placement="right">
        <ListItemButton onClick={() => onToggle(group.label)} sx={buttonSx}>
          {row(
            <ExpandMore
              sx={{
                fontSize: 18,
                color: active ? SHELL.activeText : SHELL.textMuted,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform .2s ease',
              }}
            />,
          )}
        </ListItemButton>
      </Tooltip>

      {!collapsed && (
        <Collapse in={expanded} unmountOnExit>
          <List disablePadding sx={{ mb: 0.75, mt: 0.25 }}>
            {(group.children ?? []).map((item) => (
              <SubEntry
                key={item.label}
                item={item}
                isCurrent={isCurrent}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

/** One menu item, itself optionally holding the single permitted submenu level. */
function SubEntry({ item, isCurrent, onNavigate }: {
  item: NavItem;
  isCurrent: (to: string) => boolean;
  onNavigate?: () => void;
}) {
  const hasKids = !!item.children?.length;
  const containsCurrent = hasKids && leavesOf(item.children!).some((leaf) => isCurrent(leaf.to));
  const [open, setOpen] = useState(containsCurrent);

  // Reopens itself when the route moves into it — the same reasoning as the entry above.
  useEffect(() => { if (containsCurrent) setOpen(true); }, [containsCurrent]);

  if (hasKids) {
    return (
      <>
        <ListItemButton
          onClick={() => setOpen((v) => !v)}
          sx={{
            borderRadius: 2, py: 0.75, pl: 1.75, pr: 1,
            '&:hover': { bgcolor: SHELL.hover },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: 12.5,
              fontWeight: containsCurrent ? 700 : 500,
              color: containsCurrent ? SHELL.activeText : SHELL.text,
              noWrap: true,
            }}
          />
          <ExpandMore sx={{
            fontSize: 16, color: SHELL.textMuted,
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform .2s ease',
          }} />
        </ListItemButton>

        <Collapse in={open} unmountOnExit>
          <List disablePadding sx={{ ml: 1.5 }}>
            {item.children!.map((child) => (
              <SubEntry key={child.label} item={child} isCurrent={isCurrent} onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  if (!item.to) return null;
  const current = isCurrent(item.to);

  return (
    <ListItemButton
      component={RouterLink}
      to={item.to}
      onClick={onNavigate}
      sx={{
        borderRadius: `${tk.radius.md}px`, minHeight: 34, py: 0.6, pl: 5.5, pr: 1, mb: 0.25,
        bgcolor: current ? SHELL.activeSub : 'transparent',
        '&:hover': { bgcolor: current ? SHELL.activeSub : SHELL.hover },
      }}
    >
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          fontSize: 13.5,
          fontWeight: current ? 600 : 400,
          color: current ? SHELL.activeText : SHELL.text,
          noWrap: true,
        }}
      />
      {!!item.badge && <Badge value={item.badge} active={current} />}
    </ListItemButton>
  );
}

/** The count pill beside an entry — pending enquiries, open quotes. */
function Badge({ value, active }: { value: number; active: boolean }) {
  return (
    <Box sx={{
      minWidth: 24, px: 0.75, py: '1px', borderRadius: 99, textAlign: 'center',
      fontSize: 11, fontWeight: 700,
      color: active ? SHELL.activeText : SHELL.text,
      bgcolor: active ? SHELL.activeText : alpha(SHELL.activeText, 0.12),
    }}>
      {value}
    </Box>
  );
}
